import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  Bell,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  MessageCircle,
  PlusCircle,
  Search,
  Trash2,
  UserRound
} from "lucide-react";

export default function RemindersTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>

          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>

            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} />
          <span className="text-indigo-600">Gestão e Agenda</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Lembretes e Acompanhamentos da Clínica
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span>4 min de leitura</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão e Agenda
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            Use os Lembretes para organizar retornos, aniversários, contatos pendentes,
            acompanhamentos clínicos e tarefas importantes da rotina da clínica.
          </p>

          <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-6 mt-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                <Bell className="text-indigo-600" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mt-0 mb-2">
                  O que este módulo resolve?
                </h2>
                <p className="m-0">
                  O módulo de Lembretes centraliza tarefas operacionais e contatos importantes,
                  reduzindo o risco de esquecimentos e ajudando a equipe a manter o acompanhamento
                  dos pacientes em dia.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
            Passo a Passo em Texto
          </h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <Calendar className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  1. Acessando os Lembretes
                </h3>
              </div>

              <p>
                Acesse o módulo pelo menu lateral em <strong>Lembretes</strong>.
                A tela mostra os lembretes organizados por data, facilitando a visualização
                das tarefas do dia e dos próximos compromissos operacionais.
              </p>

              <p>
                Você pode usar os filtros rápidos para visualizar:
              </p>

              <ul>
                <li><strong>Hoje:</strong> mostra apenas os lembretes do dia atual;</li>
                <li><strong>7 dias:</strong> mostra lembretes previstos para a próxima semana;</li>
                <li><strong>30 dias:</strong> mostra lembretes do mês;</li>
                <li><strong>Todos:</strong> mostra todos os lembretes cadastrados.</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <PlusCircle className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  2. Criando um Novo Lembrete
                </h3>
              </div>

              <p>
                Clique em <strong>Novo Lembrete</strong> para registrar uma nova tarefa.
                Informe um título claro, a data desejada e, se necessário, um horário.
              </p>

              <p>
                Também é possível preencher uma descrição com detalhes adicionais,
                como orientação de contato, motivo do retorno ou observação administrativa.
              </p>

              <p>
                Exemplos de lembretes úteis:
              </p>

              <ul>
                <li>Confirmar retorno de paciente;</li>
                <li>Enviar mensagem pós-operatória;</li>
                <li>Verificar pagamento pendente;</li>
                <li>Solicitar documentação complementar;</li>
                <li>Reativar paciente que está sem atendimento há algum tempo.</li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <UserRound className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  3. Vinculando o Lembrete a um Paciente
                </h3>
              </div>

              <p>
                Ao criar um lembrete, você pode pesquisar e selecionar um paciente.
                Esse vínculo é opcional, mas recomendado sempre que a tarefa estiver relacionada
                a um atendimento, retorno, cobrança, documentação ou contato específico.
              </p>

              <p>
                O vínculo com o paciente facilita a identificação da tarefa e torna o lembrete
                mais útil para a rotina da recepção e dos profissionais.
              </p>

              <div className="bg-slate-50 border rounded-2xl p-5 mt-5">
                <div className="flex items-center gap-2 font-bold text-gray-900 mb-2">
                  <Search size={18} />
                  Dica prática
                </div>
                <p className="m-0">
                  Use a busca por nome, telefone ou e-mail para localizar o paciente com mais rapidez.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <Bell className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  4. Lembretes Automáticos de Aniversário
                </h3>
              </div>

              <p>
                O OdontoAssist AI também exibe automaticamente lembretes de aniversário
                dos pacientes cadastrados com data de nascimento preenchida.
              </p>

              <p>
                Esses lembretes ajudam a clínica a manter ações de relacionamento,
                fidelização e contato humanizado com os pacientes.
              </p>

              <p>
                Para que o aniversário apareça corretamente, mantenha o campo
                <strong> Data de nascimento</strong> preenchido no cadastro do paciente.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <MessageCircle className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  5. Usando a Integração com WhatsApp
                </h3>
              </div>

              <p>
                Quando o lembrete estiver relacionado a um paciente, a equipe pode usar
                o atalho de WhatsApp Web para agilizar o contato.
              </p>

              <p>
                Essa integração funciona em conjunto com a extensão
                <strong> OdontoAssist Connect</strong>, permitindo abrir o WhatsApp Web
                com o contexto do paciente selecionado.
              </p>

              <p>
                Esse recurso é útil para confirmação de retornos, envio de orientações,
                acompanhamento pós-atendimento e mensagens administrativas.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  6. Marcando um Lembrete como Visto
                </h3>
              </div>

              <p>
                Quando a tarefa já foi conferida ou tratada, clique para marcar o lembrete
                como <strong>visto</strong>.
              </p>

              <p>
                Essa ação não deve ser confundida com exclusão. O objetivo é indicar
                que aquele lembrete já recebeu atenção, mantendo a organização da tela
                e preservando melhor o controle operacional.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <div className="flex items-center gap-3 mb-3">
                <Trash2 className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900 m-0">
                  7. Excluindo Lembretes
                </h3>
              </div>

              <p>
                Use a exclusão quando o lembrete não for mais necessário ou tiver sido
                criado por engano.
              </p>

              <p>
                Antes de excluir, o sistema solicita confirmação para evitar remoções acidentais.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />

              <h3 className="text-xl font-bold text-gray-900 mb-3">
                8. Boas Práticas de Uso
              </h3>

              <ul>
                <li>Use títulos objetivos, como “Confirmar retorno” ou “Enviar orientação pós-operatória”.</li>
                <li>Vincule o paciente sempre que o lembrete estiver relacionado a um prontuário.</li>
                <li>Use a descrição para registrar detalhes importantes da tarefa.</li>
                <li>Revise diariamente os lembretes de hoje.</li>
                <li>Use o filtro de 7 dias para planejar a semana da clínica.</li>
                <li>Marque como visto apenas quando a tarefa já tiver sido conferida.</li>
                <li>Exclua somente lembretes que não precisam mais permanecer no controle operacional.</li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-white border rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mt-0 mb-4">
              Resumo
            </h2>

            <p>
              O módulo de Lembretes ajuda a clínica a manter uma rotina mais organizada,
              reunindo tarefas manuais, aniversários automáticos e acompanhamentos de pacientes
              em uma tela simples e objetiva.
            </p>

            <p>
              Usado em conjunto com o prontuário, a agenda e a extensão do WhatsApp Web,
              ele melhora o controle dos retornos, contatos e pendências da equipe.
            </p>
          </div>
        </article>
      </main>
    </div>
  );
}
